<template>
  <card shadow>
    <template v-slot:header>
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">Employee Payouts</h3>
          <p class="text-muted text-sm mb-0">
            Click on an employee to see their tip breakdown
          </p>
        </div>
        <div class="col-auto d-flex align-items-center">
          <input
            type="text"
            v-model="searchQuery"
            class="form-control form-control-sm mr-3"
            placeholder="Search by name, email, staff ID..."
            style="width: 250px"
          />
          <button class="btn btn-sm btn-success" @click="exportData">
            <i class="fas fa-download mr-1"></i>Export
          </button>
        </div>
      </div>
    </template>

    <div class="table-responsive">
      <table class="table align-items-center table-flush table-hover">
        <thead class="thead-light">
          <tr>
            <sortable-header
              sort-key="name"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Employee
            </sortable-header>
            <sortable-header
              sort-key="totalAmount"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Total Payout
            </sortable-header>
            <sortable-header
              sort-key="uniqueTipCount"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Tips
            </sortable-header>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="emp in paginatedData"
            :key="emp.staffId || emp.paycomCode"
            class="clickable-row"
            @click="$emit('select', emp)"
          >
            <td class="align-top">
              <strong>{{ emp.name }}</strong>
              <small class="text-muted d-block" style="line-height: 1.2">{{
                emp.email
              }}</small>
              <small class="text-muted" style="line-height: 1.2">
                Staff ID:
                <copy-text :text="emp.paycomCode" label="Staff ID" inline>
                  <span class="text-monospace">{{ emp.paycomCode }}</span>
                </copy-text>
              </small>
            </td>
            <td class="align-top">
              <strong class="text-success">{{
                formatCurrency(emp.totalAmount)
              }}</strong>
            </td>
            <td class="align-top">{{ emp.uniqueTipCount }}</td>
            <td class="align-top">
              <button
                class="btn btn-sm btn-outline-primary"
                @click.stop="$emit('select', emp)"
              >
                <i class="fas fa-search"></i> Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <table-pagination
      v-model:current-page="currentPage"
      :total-items="filteredData.length"
      :per-page="perPage"
      item-label="employees"
    />
  </card>
</template>

<script>
import CopyText from "@/components/CopyText";
import TablePagination from "@/components/TablePagination";
import SortableHeader from "@/components/SortableHeader";
import { formatCurrency, exportToCSV } from "@/utils/formatters";

export default {
  name: "EmployeeTab",
  components: {
    CopyText,
    TablePagination,
    SortableHeader,
  },
  props: {
    employees: {
      type: Array,
      required: true,
    },
    reportId: {
      type: String,
      required: true,
    },
  },
  emits: ["select"],
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      perPage: 15,
      sortState: { key: "totalAmount", order: "desc" },
    };
  },
  computed: {
    filteredData() {
      let result = [...this.employees];

      // Search filter
      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(
          (emp) =>
            emp.name?.toLowerCase().includes(search) ||
            emp.email?.toLowerCase().includes(search) ||
            emp.paycomCode?.toLowerCase().includes(search) ||
            emp.phone?.includes(search)
        );
      }

      // Sort
      result.sort((a, b) => {
        let aVal = a[this.sortState.key];
        let bVal = b[this.sortState.key];

        if (
          this.sortState.key === "totalAmount" ||
          this.sortState.key === "uniqueTipCount"
        ) {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }

        if (aVal < bVal) return this.sortState.order === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortState.order === "asc" ? 1 : -1;
        return 0;
      });

      return result;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredData.slice(start, start + this.perPage);
    },
  },
  methods: {
    formatCurrency,
    handleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.order = this.sortState.order === "asc" ? "desc" : "asc";
      } else {
        this.sortState.key = key;
        this.sortState.order = "asc";
      }
    },
    exportData() {
      exportToCSV({
        data: this.filteredData,
        columns: [
          { header: "Name", value: "name" },
          { header: "Staff Id", value: "paycomCode" },
          { header: "Email", value: "email" },
          { header: "Phone", value: "phone" },
          {
            header: "Total Payout",
            value: (emp) => (emp.totalAmount / 100).toFixed(2),
          },
          { header: "Tips", value: "uniqueTipCount" },
          { header: "Shares", value: "shareCount" },
          { header: "Locations", value: (emp) => emp.locations.join("; ") },
        ],
        filename: `employee-payouts-${this.reportId}.csv`,
      });
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.clickable-row {
  cursor: pointer;

  &:hover {
    background-color: #f8f9fe !important;
  }
}

.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.8rem;
}
</style>
